import { useMemo } from 'react';

import { CompanyEntry } from '@dreryk/egabinet-invoice-frontend-api';

import { BindingCreator, createTranslatedText, FormContextValues } from '@dreryk/react-common';
import { Select } from '@dreryk/react-components';

import { invoiceSharedComponentsTranslationKeys } from '../../../translations';
import { InvoiceCreateFormAddressSectionBeanPart } from '../addressSection/InvoiceCreateFormAddressSection.types';

import { InvoiceCreateFormCompanyIdentificationDetailsFormBeanPart } from './InvoiceCreateFormCompanyIdentificationDetails.types';
import { InvoiceCreateFormCompanyNameFormBeanPart } from './InvoiceCreateFormCompanyName.types';

const COMPANY_ENTRY_FIELD_NAME = 'companyName';
const COMPANY_NAME_MAX_LENGTH = 255;

export type CompanyEntryFormBeanPart = {
   [COMPANY_ENTRY_FIELD_NAME]: CompanyEntry;
};

const triggerLabelExtractor = (companyEntry: CompanyEntry): string => (companyEntry ? companyEntry.name : '');
const keyExtractor = (companyEntry: CompanyEntry): string => companyEntry.name;

type InvoiceCreateFormCompanyNameSelectProps = {
   readonly companyEntries: CompanyEntry[] | undefined;
   readonly register: BindingCreator<InvoiceCreateFormCompanyNameFormBeanPart, typeof COMPANY_ENTRY_FIELD_NAME>;
} & Pick<
   FormContextValues<
      InvoiceCreateFormCompanyIdentificationDetailsFormBeanPart & InvoiceCreateFormAddressSectionBeanPart
   >,
   'getValues' | 'reset'
>;

export const InvoiceCreateFormCompanyNameSelect = (props: InvoiceCreateFormCompanyNameSelectProps): JSX.Element => {
   const { companyEntries, getValues, register, reset } = props;

   const items = useMemo(() => companyEntries ?? [], [companyEntries]);

   const handleSelect = (companyEntry: CompanyEntry | null): void => {
      if (!companyEntry) {
         return;
      }

      reset({
         ...getValues(),
         building: companyEntry.partialAddress?.findBuilding() ?? null,
         companyName: companyEntry,
         country: companyEntry.partialAddress?.getCountry(),
         flat: companyEntry.partialAddress?.findFlat() ?? null,
         postcode: companyEntry.partialAddress?.findPostcode() ?? null,
         street: companyEntry.partialAddress?.findStreet() ?? null,
         taxIdentifier: companyEntry.taxIdentifier.toString(),
         town: companyEntry.partialAddress?.findTown() ?? null,
      });
   };

   return (
      <Select<CompanyEntry>
         controller={register(COMPANY_ENTRY_FIELD_NAME, {
            required: true,
            maxLength: COMPANY_NAME_MAX_LENGTH,
         })}
         testId={COMPANY_ENTRY_FIELD_NAME}
         triggerLabelExtractor={triggerLabelExtractor}
         label={invoiceSharedComponentsTranslationKeys.companyName}
         onSelect={handleSelect}
      >
         <Select.List items={items} keyExtractor={keyExtractor}>
            {(companyEntry) => (
               <Select.Item
                  item={companyEntry}
                  testId={companyEntry.taxIdentifier.toString()}
                  label={createTranslatedText(companyEntry.name)}
               />
            )}
         </Select.List>
      </Select>
   );
};
